export type Language = 'fr' | 'en' | 'es';

export const translations = {
  fr: {
    navbar: {
      features: 'Fonctionnalités',
      pricing: 'Tarifs',
      faq: 'FAQ',
      login: 'Se connecter',
      cta: 'Essai gratuit',
    },
    hero: {
      badge: 'Nouvelle génération de RAG disponible',
      title_prefix: 'Donnez une voix à votre',
      title_highlight: "savoir d'entreprise",
      subtitle: "Transformez vos documents, emails et bases de données en agents intelligents. Automatisez le support client et boostez la productivité interne sans effort technique.",
      cta_primary: 'Démarrer gratuitement',
      cta_secondary: 'Voir la démo',
      social_proof: 'Déjà adopté par des équipes innovantes',
      mini_features: {
        instant: { title: 'Réponses instantanées', desc: 'Plus de temps d\'attente.' },
        sources: { title: 'Multi-Sources (RAG)', desc: 'PDF, Notion, Web, Drive.' },
        security: { title: 'Sécurité Enterprise', desc: 'Données privées et isolées.' },
      }
    },
    features: {
      section_title: 'Tout-en-un',
      main_title: 'Une plateforme complète pour vos agents IA',
      description: 'Totemium centralise la création, le déploiement et le monitoring de vos agents conversationnels. Plus besoin de jongler entre 5 outils différents.',
      cards: {
        rag: {
          title: 'RAG Multi-Sources',
          desc: "Ingérez n'importe quel document d'entreprise. Totemium indexe vos PDFs, Word, sites web, vidéos YouTube et Notion pour que vos agents aient réponse à tout, avec citations."
        },
        whatsapp: {
          title: 'WhatsApp Natif',
          desc: "Déployez votre agent sur WhatsApp en un clic. Vos clients discutent avec votre IA comme avec un ami."
        },
        security: {
          title: 'Données Sécurisées',
          desc: "Vos documents sont chiffrés et isolés. Aucune donnée ne sert à entraîner des modèles publics."
        }
      },
      badges: {
        speed: 'Réponses < 2s',
        languages: '95+ Langues',
        models: 'Ollama & Open Source',
        citations: 'Citations Sourcées'
      }
    },
    pricing: {
      section_title: 'Tarifs simples et transparents',
      main_title: 'Choisissez le plan adapté à votre croissance',
      monthly: 'Mensuel',
      annually: 'Annuel',
      discount_label: '(1 mois offert)',
      suffix: '/mois',
      tiers: {
        free: {
          name: 'Free / Sandbox',
          desc: 'Démarrez gratuitement en testant votre premier chatbot Totemium.',
          cta: 'Essayer',
          features: ['1 chatbot', '200 messages / mois', '10 sources (web/fichiers)', '10 Mo / fichier', 'Widget avec branding', 'Sans API']
        },
        starter: {
          name: 'Starter',
          desc: 'La rampe de lancement pour mettre vos équipes en autonomie.',
          cta: 'Lancer mon équipe',
          features: ['2 chatbots', '2 000 messages / mois', '200 pages crawlées', '100 fichiers (≤10 Mo)', 'Export des conversations', 'Widget embarqué']
        },
        growth: {
          name: 'Growth',
          desc: "Support prioritaire, API et intégrations pour scaler vos cas d'usage.",
          cta: 'Choisir Growth',
          features: ['5 chatbots', '10 000 messages / mois', '2 000 pages', '1 Go de fichiers', 'API + Webhooks']
        },
        scale: {
          name: 'Scale',
          desc: 'Le package complet pour les équipes exigeantes avec SLA avancé.',
          cta: 'Déployer à grande échelle',
          features: ['10 chatbots', '50 000 messages / mois', '10 000 pages', '5 Go fichiers', 'SLA p95 < 8 s', 'Domaines multiples', 'Branding supprimé']
        }
      },
      footer_overage: 'Dépassements : 5 € / 1 000 messages'
    },
    faq: {
      title: 'Questions fréquentes',
      items: [
        {
          q: "Comment Totemium apprend-il de mes documents ?",
          a: "Vous uploadez vos PDF, Word ou connectez votre site web. Notre technologie RAG (Retrieval Augmented Generation) indexe ces informations sécuritairement. L'IA consulte ensuite cette index pour répondre aux questions, sans jamais inventer."
        },
        {
          q: "Est-ce que mes données sont partagées ?",
          a: "Non. Vos données sont strictement isolées dans votre environnement. Elles ne sont jamais utilisées pour entraîner les modèles publics d'OpenAI ou autres. Vous restez propriétaire de votre savoir."
        },
        {
          q: "Puis-je utiliser mon propre numéro WhatsApp ?",
          a: "Absolument. Vous pouvez connecter votre numéro WhatsApp Business existant. L'IA prendra le relais, et vous pourrez reprendre la main (Handover) à tout moment si nécessaire."
        },
        {
          q: "Quelle est la différence entre les forfaits ?",
          a: "Le volume de messages et le nombre de sources sont les principales différences. Le plan Growth ajoute l'accès API pour intégrer Totemium dans vos propres logiciels."
        }
      ]
    },
    final_cta: {
      title: 'Prêt à transformer votre business ?',
      subtitle: "Commencez dès aujourd'hui.",
      desc: "Rejoignez les entreprises qui automatisent leur support et valorisent leur savoir avec Totemium. Sans carte bancaire.",
      btn: 'Créer mon compte gratuit'
    },
    footer: {
      tagline: "L'intelligence artificielle au service de votre entreprise. Automatisez, répondez, convertissez.",
      columns: {
        product: 'Produit',
        support: 'Support',
        legal: 'Légal'
      },
      links: {
        features: 'Fonctionnalités',
        pricing: 'Tarifs',
        integrations: 'Intégrations',
        docs: 'Documentation',
        contact: 'Contact',
        privacy: 'Confidentialité',
        terms: 'CGU'
      },
      rights: 'Tous droits réservés.'
    }
  },
  en: {
    navbar: {
      features: 'Features',
      pricing: 'Pricing',
      faq: 'FAQ',
      login: 'Log in',
      cta: 'Free Trial',
    },
    hero: {
      badge: 'New Generation RAG Available',
      title_prefix: 'Give a voice to your',
      title_highlight: "corporate knowledge",
      subtitle: "Transform your documents, emails, and databases into intelligent agents. Automate customer support and boost internal productivity with zero technical effort.",
      cta_primary: 'Start for free',
      cta_secondary: 'Watch demo',
      social_proof: 'Trusted by innovative teams',
      mini_features: {
        instant: { title: 'Instant Answers', desc: 'No more waiting times.' },
        sources: { title: 'Multi-Source (RAG)', desc: 'PDF, Notion, Web, Drive.' },
        security: { title: 'Enterprise Security', desc: 'Private and isolated data.' },
      }
    },
    features: {
      section_title: 'All-in-one',
      main_title: 'A complete platform for your AI agents',
      description: 'Totemium centralizes creation, deployment, and monitoring of your conversational agents. No need to juggle between 5 different tools.',
      cards: {
        rag: {
          title: 'Multi-Source RAG',
          desc: "Ingest any corporate document. Totemium indexes your PDFs, Word docs, websites, YouTube videos, and Notion pages so your agents have answers to everything, with citations."
        },
        whatsapp: {
          title: 'Native WhatsApp',
          desc: "Deploy your agent on WhatsApp in one click. Your customers chat with your AI just like with a friend."
        },
        security: {
          title: 'Secure Data',
          desc: "Your documents are encrypted and isolated. No data is used to train public models."
        }
      },
      badges: {
        speed: 'Answers < 2s',
        languages: '95+ Languages',
        models: 'Ollama & Open Source',
        citations: 'Sourced Citations'
      }
    },
    pricing: {
      section_title: 'Simple and transparent pricing',
      main_title: 'Choose the plan that fits your growth',
      monthly: 'Monthly',
      annually: 'Yearly',
      discount_label: '(1 month free)',
      suffix: '/month',
      tiers: {
        free: {
          name: 'Free / Sandbox',
          desc: 'Start for free by testing your first Totemium chatbot.',
          cta: 'Try now',
          features: ['1 chatbot', '200 messages / month', '10 sources (web/files)', '10 MB / file', 'Branded widget', 'No API']
        },
        starter: {
          name: 'Starter',
          desc: 'The launchpad to get your teams autonomous.',
          cta: 'Launch my team',
          features: ['2 chatbots', '2,000 messages / month', '200 crawled pages', '100 files (≤10 MB)', 'Conversation export', 'Embedded widget']
        },
        growth: {
          name: 'Growth',
          desc: "Priority support, API, and integrations to scale your use cases.",
          cta: 'Choose Growth',
          features: ['5 chatbots', '10,000 messages / month', '2,000 pages', '1 GB of files', 'API + Webhooks']
        },
        scale: {
          name: 'Scale',
          desc: 'The complete package for demanding teams with advanced SLA.',
          cta: 'Deploy at scale',
          features: ['10 chatbots', '50,000 messages / month', '10,000 pages', '5 GB files', 'SLA p95 < 8s', 'Multiple domains', 'Branding removed']
        }
      },
      footer_overage: 'Overage: 5 € / 1,000 messages'
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          q: "How does Totemium learn from my documents?",
          a: "You upload your PDF, Word docs or connect your website. Our RAG (Retrieval Augmented Generation) technology securely indexes this information. The AI then consults this index to answer questions, without ever hallucinating."
        },
        {
          q: "Is my data shared?",
          a: "No. Your data is strictly isolated in your environment. It is never used to train public models from OpenAI or others. You remain the owner of your knowledge."
        },
        {
          q: "Can I use my own WhatsApp number?",
          a: "Absolutely. You can connect your existing WhatsApp Business number. The AI will take over, and you can take back control (Handover) at any time if needed."
        },
        {
          q: "What is the difference between plans?",
          a: "Message volume and number of sources are the main differences. The Growth plan adds API access to integrate Totemium into your own software."
        }
      ]
    },
    final_cta: {
      title: 'Ready to transform your business?',
      subtitle: "Start today.",
      desc: "Join companies automating their support and leveraging their knowledge with Totemium. No credit card required.",
      btn: 'Create free account'
    },
    footer: {
      tagline: "Artificial intelligence serving your business. Automate, answer, convert.",
      columns: {
        product: 'Product',
        support: 'Support',
        legal: 'Legal'
      },
      links: {
        features: 'Features',
        pricing: 'Pricing',
        integrations: 'Integrations',
        docs: 'Documentation',
        contact: 'Contact',
        privacy: 'Privacy',
        terms: 'Terms'
      },
      rights: 'All rights reserved.'
    }
  },
  es: {
    navbar: {
      features: 'Características',
      pricing: 'Precios',
      faq: 'FAQ',
      login: 'Conectarse',
      cta: 'Prueba gratis',
    },
    hero: {
      badge: 'Nueva generación RAG disponible',
      title_prefix: 'Da voz a tu',
      title_highlight: "conocimiento corporativo",
      subtitle: "Transforma tus documentos, correos y bases de datos en agentes inteligentes. Automatiza el soporte al cliente y aumenta la productividad interna sin esfuerzo técnico.",
      cta_primary: 'Empezar gratis',
      cta_secondary: 'Ver demo',
      social_proof: 'Con la confianza de equipos innovadores',
      mini_features: {
        instant: { title: 'Respuestas instantáneas', desc: 'Sin tiempos de espera.' },
        sources: { title: 'Multi-Fuentes (RAG)', desc: 'PDF, Notion, Web, Drive.' },
        security: { title: 'Seguridad Empresarial', desc: 'Datos privados y aislados.' },
      }
    },
    features: {
      section_title: 'Todo en uno',
      main_title: 'Una plataforma completa para tus agentes IA',
      description: 'Totemium centraliza la creación, despliegue y monitoreo de tus agentes conversacionales. No más malabarismos entre 5 herramientas diferentes.',
      cards: {
        rag: {
          title: 'RAG Multi-Fuentes',
          desc: "Ingiere cualquier documento corporativo. Totemium indexa tus PDFs, Word, sitios web, videos de YouTube y Notion para que tus agentes tengan respuesta a todo, con citas."
        },
        whatsapp: {
          title: 'WhatsApp Nativo',
          desc: "Despliega tu agente en WhatsApp con un clic. Tus clientes chatean con tu IA como con un amigo."
        },
        security: {
          title: 'Datos Seguros',
          desc: "Tus documentos están cifrados y aislados. Ningún dato se utiliza para entrenar modelos públicos."
        }
      },
      badges: {
        speed: 'Respuestas < 2s',
        languages: '95+ Idiomas',
        models: 'Ollama y Open Source',
        citations: 'Citas con Fuente'
      }
    },
    pricing: {
      section_title: 'Precios simples y transparentes',
      main_title: 'Elige el plan adaptado a tu crecimiento',
      monthly: 'Mensual',
      annually: 'Anual',
      discount_label: '(1 mes gratis)',
      suffix: '/mes',
      tiers: {
        free: {
          name: 'Free / Sandbox',
          desc: 'Empieza gratis probando tu primer chatbot Totemium.',
          cta: 'Probar',
          features: ['1 chatbot', '200 mensajes / mes', '10 fuentes (web/archivos)', '10 MB / archivo', 'Widget con marca', 'Sin API']
        },
        starter: {
          name: 'Starter',
          desc: 'La rampa de lanzamiento para dar autonomía a tus equipos.',
          cta: 'Lanzar mi equipo',
          features: ['2 chatbots', '2,000 mensajes / mes', '200 páginas rastreadas', '100 archivos (≤10 MB)', 'Exportación de conversaciones', 'Widget integrado']
        },
        growth: {
          name: 'Growth',
          desc: "Soporte prioritario, API e integraciones para escalar tus casos de uso.",
          cta: 'Elegir Growth',
          features: ['5 chatbots', '10,000 mensajes / mes', '2,000 páginas', '1 GB de archivos', 'API + Webhooks']
        },
        scale: {
          name: 'Scale',
          desc: 'El paquete completo para equipos exigentes con SLA avanzado.',
          cta: 'Desplegar a escala',
          features: ['10 chatbots', '50,000 mensajes / mes', '10,000 páginas', '5 GB archivos', 'SLA p95 < 8s', 'Dominios múltiples', 'Marca eliminada']
        }
      },
      footer_overage: 'Exceso: 5 € / 1,000 mensajes'
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          q: "¿Cómo aprende Totemium de mis documentos?",
          a: "Subes tus PDF, Word o conectas tu sitio web. Nuestra tecnología RAG (Retrieval Augmented Generation) indexa esta información de forma segura. La IA consulta este índice para responder preguntas, sin inventar nunca."
        },
        {
          q: "¿Se comparten mis datos?",
          a: "No. Tus datos están estrictamente aislados en tu entorno. Nunca se utilizan para entrenar modelos públicos de OpenAI u otros. Sigues siendo el dueño de tu conocimiento."
        },
        {
          q: "¿Puedo usar mi propio número de WhatsApp?",
          a: "Absolutamente. Puedes conectar tu número de WhatsApp Business existente. La IA tomará el relevo y podrás retomar el control (Handover) en cualquier momento si es necesario."
        },
        {
          q: "¿Cuál es la diferencia entre los planes?",
          a: "El volumen de mensajes y el número de fuentes son las principales diferencias. El plan Growth añade acceso API para integrar Totemium en tu propio software."
        }
      ]
    },
    final_cta: {
      title: '¿Listo para transformar tu negocio?',
      subtitle: "Empieza hoy mismo.",
      desc: "Únete a las empresas que automatizan su soporte y valoran su conocimiento con Totemium. Sin tarjeta de crédito.",
      btn: 'Crear cuenta gratuita'
    },
    footer: {
      tagline: "Inteligencia artificial al servicio de tu empresa. Automatiza, responde, convierte.",
      columns: {
        product: 'Producto',
        support: 'Soporte',
        legal: 'Legal'
      },
      links: {
        features: 'Características',
        pricing: 'Precios',
        integrations: 'Integraciones',
        docs: 'Documentación',
        contact: 'Contacto',
        privacy: 'Privacidad',
        terms: 'Términos'
      },
      rights: 'Todos los derechos reservados.'
    }
  }
};
